import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useEffect, useCallback } from 'react';

import type { BlogPost, BlogPostsResponse } from '../../services/api';
import api from '../../services/api';

import styles from './BlogList.module.css';
import FullscreenBlogPost from './FullscreenBlogPost';

/**
 * BlogList Props
 */
type BlogListProps = {
  readonly pageSlug: string;
  readonly settings?: {
    readonly postsPerPage?: number;
    readonly showExcerpts?: boolean;
    readonly showFeaturedImages?: boolean;
  };
};

/**
 * BlogList Component
 *
 * Displays a grid of blog post cards for public viewing.
 * Features:
 * - Grid layout of post cards
 * - Featured image, title, excerpt, date
 * - Click to open fullscreen post reader
 * - Pagination with load more
 * - Framer Motion animations
 */
const BlogList: React.FC<BlogListProps> = ({ pageSlug, settings }) => {
  const [posts, setPosts] = useState<ReadonlyArray<BlogPost>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const postsPerPage = settings?.postsPerPage ?? 10;
  const showExcerpts = settings?.showExcerpts ?? true;
  const showFeaturedImages = settings?.showFeaturedImages ?? true;

  /**
   * Fetch posts from API
   */
  const fetchPosts = useCallback(
    async (pageNum: number, append: boolean = false) => {
      try {
        setLoading(true);
        setError(null);

        const response: BlogPostsResponse = await api.blog.getByPage(pageSlug, pageNum, postsPerPage);

        if (append) {
          setPosts((prev) => [...prev, ...response.posts]);
        } else {
          setPosts(response.posts);
        }
        setTotalPages(response.totalPages);
        setPage(pageNum);
      } catch (err) {
        const error = err as { readonly message?: string };
        console.error('Failed to fetch blog posts:', err);
        setError(error.message ?? 'Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    },
    [pageSlug, postsPerPage]
  );

  // Initial fetch
  useEffect(() => {
    fetchPosts(1);
  }, [fetchPosts]);

  /**
   * Load more posts
   */
  const handleLoadMore = () => {
    if (page < totalPages) {
      fetchPosts(page + 1, true);
    }
  };

  /**
   * Open post in fullscreen
   */
  const handlePostClick = (post: BlogPost, index: number) => {
    setSelectedPost(post);
    setSelectedIndex(index);
  };

  /**
   * Close fullscreen post
   */
  const handleClosePost = () => {
    setSelectedPost(null);
  };

  /**
   * Navigate to next post
   */
  const handleNextPost = () => {
    if (selectedIndex < posts.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedPost(posts[selectedIndex + 1]);
    }
  };

  /**
   * Navigate to previous post
   */
  const handlePrevPost = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedPost(posts[selectedIndex - 1]);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Loading state
  if (loading && posts.length === 0) {
    return (
      <div className={styles.blogList}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading posts...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && posts.length === 0) {
    return (
      <div className={styles.blogList}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <button onClick={() => fetchPosts(1)} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!loading && posts.length === 0) {
    return (
      <div className={styles.blogList}>
        <div className={styles.emptyState}>
          <p>No blog posts yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.blogList}>
      <div className={styles.postsContainer}>
        <AnimatePresence mode="popLayout">
          {posts.map((post, index) => (
            <motion.article
              key={post._id}
              className={styles.postCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              onClick={() => handlePostClick(post, index)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {showFeaturedImages && post.featuredImage?.url && (
                <div className={styles.imageWrapper}>
                  <img
                    src={post.featuredImage.url}
                    alt={post.featuredImage.alt ?? post.title}
                    className={styles.featuredImage}
                  />
                </div>
              )}
              <div className={styles.postContent}>
                <h3 className={styles.postTitle}>{post.title}</h3>
                {post.publishedAt && (
                  <time className={styles.postDate}>{formatDate(post.publishedAt)}</time>
                )}
                {showExcerpts && post.excerpt && (
                  <p className={styles.postExcerpt}>{post.excerpt}</p>
                )}
              </div>
            </motion.article>
          ))}
        </AnimatePresence>
      </div>

      {page < totalPages && (
        <div className={styles.loadMoreContainer}>
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className={styles.loadMoreButton}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {/* Fullscreen Post Reader */}
      {selectedPost && (
        <FullscreenBlogPost
          post={selectedPost}
          isOpen={!!selectedPost}
          onClose={handleClosePost}
          onNext={selectedIndex < posts.length - 1 ? handleNextPost : undefined}
          onPrevious={selectedIndex > 0 ? handlePrevPost : undefined}
        />
      )}
    </div>
  );
};

export default BlogList;
